import { CSSResultGroup, TemplateResult, html, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './tufts-quote-block.css.lit';
import { ResizeController } from '../../controllers/resize-controller';
import { SlotNGController } from '../../controllers/slot-ng-controller';
import { classMap } from 'lit/directives/class-map.js';

/**
 * Tufts Quote Block
 * @element tufts-quote-block
 *
 * @slot quote
 * @slot name
 * @slot bio
 * @slot image
 */

@customElement('tufts-quote-block')
export class tuftsQuoteBlock extends OutlineElement {
  ngSlot = new SlotNGController(this);
  static styles: CSSResultGroup = [componentStyles];

  @state() private hasImageGridSlot: boolean;
  resizeController: ResizeController = new ResizeController(this);

  @property({ type: String, attribute: 'variant', reflect: true })
  variant = 'dark';

  @property({ type: Boolean, reflect: true })
  limited = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.hasImageGridSlot = this.ngSlot.exist('image');
  }

  firstUpdated(): void {
    if (this.limited) {
      this.limitQuoteText();
    }
  }

  /**
   * Limits the quote text to 150 characters and adds ellipsis if needed
   */
  limitQuoteText(): void {
    // Wait for the DOM to be ready
    setTimeout(() => {
      const quoteSlot = this.querySelector('[slot="quote"]');
      if (quoteSlot && this.limited) {
        const originalText = quoteSlot.textContent || '';
        if (originalText.length > 150) {
          // Store original text as data attribute
          quoteSlot.setAttribute('data-original-text', originalText);
          // Truncate text to 150 characters and add ellipsis
          quoteSlot.textContent = originalText.substring(0, 150) + '...';
        }
      }
    }, 0);
  }

  updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('limited') && this.limited) {
      this.limitQuoteText();
    }
  }

  render(): TemplateResult {
    const classes = {
      'container': true,
      'mobile': this.resizeController.currentBreakpointRange === 0,
      'image-grid': this.hasImageGridSlot,
      [this.variant]: this.variant,
      'limited-quote': this.limited,
    };
    return html`
      <div class="${classMap(classes)}">
        ${this.iconTemplate()}
        <div class="text-wrapper">
          ${this.ngSlot.conditionalNGSlot('quote')}
          ${this.ngSlot.conditionalNGSlot('name')}
          ${this.ngSlot.conditionalNGSlot('bio')}
        </div>
        ${this.ngSlot.conditionalNGSlot('image')}
      </div>
    `;
  }

  /**
   * @returns A TemplateResult of the Icon
   */

  iconTemplate(): TemplateResult {
    return html`
      <div class="icon">
        <div class="quote-marks">
          <svg
            width="21"
            height="36"
            viewBox="0 0 21 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0 0H20.1951V17.0303C20.1951 22.0566 18.5438 26.2572 15.1928 29.5458C11.8576 32.8187 7.16626 34.8795 1.22362 35.8087L0 36V27.65L0.949398 27.5589C3.53704 27.3107 5.51814 26.3266 6.97957 24.63L6.98017 24.6293C8.45123 22.9247 9.2256 20.6907 9.2256 17.843V15.9372H0V0ZM2.10836 2.02432V13.9129H11.334V17.843C11.334 21.0563 10.4519 23.7787 8.60426 25.9202C6.94652 27.8447 4.759 29.0143 2.10836 29.4503V33.5989C7.13677 32.6414 10.9665 30.7986 13.6858 28.1301C16.6056 25.2647 18.0868 21.5936 18.0868 17.0303V2.02432H2.10836Z"
              fill="white"
            />
          </svg>
          <svg
            width="21"
            height="36"
            viewBox="0 0 21 36"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1.38933 0H20.535V17.0313C20.535 22.0578 18.891 26.2587 15.5549 29.5474C12.2326 32.8225 7.53019 34.8823 1.55633 35.8109L0.339844 36V27.6515L1.28501 27.5604C3.86156 27.3122 5.85271 26.3271 7.33926 24.6245L7.33986 24.6238C8.83097 22.919 9.61431 20.6874 9.61431 17.8439V15.9381H0.339844V1.01222H1.38933V0ZM2.43882 2.02443V13.9136H11.7133V17.8439C11.7133 21.0618 10.8176 23.787 8.94479 25.9285C7.26902 27.8478 5.07959 29.0163 2.43882 29.452V33.6031C7.5052 32.6458 11.3466 30.8011 14.0546 28.1316C16.9615 25.266 18.436 21.5948 18.436 17.0313V2.02443H2.43882Z"
              fill="white"
            />
          </svg>
        </div>
      </div>
    `;
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'tufts-quote-block': tuftsQuoteBlock;
  }
}
